/**
 * Google Ads Performance dashboard page.
 * Passes Google-specific config to the generic PlatformDashboard.
 */
import React from 'react';
import { useTranslation } from 'src/arb-wrapper';
import type { PaidMediaInsightsConfig } from 'src/components/paid-media-insights/InsightsSection';
import PlatformDashboard from 'src/components/paid-media-dashboard/PlatformDashboard';
import { formatPercent } from 'src/utils/dashboardUtils';

const GOOGLE_TABS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'adgroup', label: 'Ad Group' },
];

const GOOGLE_COLUMN_WIDTHS: Record<string, number> = {
  campaign_name: 600,
  adgroup_name: 600,
};

const GOOGLE_FORMAT_OVERRIDES = {
  ctr: (value: unknown) => formatPercent(value),
  interaction_rate: (value: unknown) => formatPercent(value),
};

const GOOGLE_SUMMARY_COLUMNS = [
  'campaign_name',
  'adgroup_name',
  'descriptive_name',
  'currency_code',
  'spend',
  'impressions',
  'clicks',
  'ctr',
];

const GOOGLE_ALWAYS_VISIBLE_KEYS = ['campaign_name', 'adgroup_name'];

const GOOGLE_CURRENCY_COLUMN_KEY = 'currency_code';

const GOOGLE_INSIGHTS_CONFIG = {
  cardMetrics: [
    {
      key: 'spend',
      label: 'paid_media_insights_metric_spend',
      numberFormat: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      },
    },
    {
      key: 'impressions',
      label: 'paid_media_insights_metric_impressions',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'clicks',
      label: 'paid_media_insights_metric_clicks',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'ctr',
      label: 'paid_media_insights_metric_ctr',
      numberFormat: { maximumFractionDigits: 2, style: 'percent' },
    },
  ],
  engagementMetrics: [
    { key: 'interactions', label: 'paid_media_insights_metric_interactions' },
    { key: 'video_trueview_views', label: 'paid_media_insights_metric_video_trueview_views' },
  ],
} satisfies PaidMediaInsightsConfig;

const GoogleDashboard: React.FC = () => {
  const { t } = useTranslation();

  const columnTooltips: Record<string, string> = {
    campaign_name: t('paid_media_tooltip_campaign_name'),
    spend: t('paid_media_tooltip_spend'),
    impressions: t('paid_media_tooltip_impressions'),
    clicks: t('paid_media_tooltip_clicks'),
    ctr: t('paid_media_tooltip_ctr'),
    cpc: t('paid_media_tooltip_cpc'),
    cpm: t('paid_media_tooltip_cpm'),
  };

  return (
    <PlatformDashboard
      alwaysVisibleKeys={GOOGLE_ALWAYS_VISIBLE_KEYS}
      columnTooltips={columnTooltips}
      columnWidths={GOOGLE_COLUMN_WIDTHS}
      currencyColumnKey={GOOGLE_CURRENCY_COLUMN_KEY}
      filterLabelOverrides={{ advertiserId: 'Account' }}
      formatOverrides={GOOGLE_FORMAT_OVERRIDES}
      insightsConfig={GOOGLE_INSIGHTS_CONFIG}
      summaryColumns={GOOGLE_SUMMARY_COLUMNS}
      tabs={GOOGLE_TABS}
      title="Google Ads Performance"
      view="google"
    />
  );
};

export default GoogleDashboard;
